import { Link } from "react-router-dom";
import { kaspreadsheetUrl } from "@/mocks/categories";
import { categories } from "@/mocks/categories";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageMeta from "@/components/base/PageMeta";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://usfansspreadsheet.help";
const pagePath = "/przewodnik-zakupowy";
const pageTitle = "Przewodnik zakupowy USFansSpreadsheet";
const pageDesc = "Wszystko, co musisz wiedzieć przed zakupem produktów USFansSpreadsheet w oficjalnym sklepie Kaspreadsheet. Znajdź idealne produkty wśród naszych kategorii.";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": pageTitle,
  "description": pageDesc,
  "author": { "@type": "Person", "name": "Anna Kowalska", "jobTitle": "Specjalista SEO" },
  "datePublished": "2026-06-15",
  "dateModified": "2026-06-24",
  "publisher": { "@type": "Organization", "name": "USFansSpreadsheet.help", "url": siteUrl },
  "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}${pagePath}` },
  "inLanguage": "pl-PL"
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Strona główna", "item": siteUrl },
    { "@type": "ListItem", "position": 2, "name": "Przewodnik zakupowy" }
  ]
};

export default function ShoppingGuide() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <PageMeta
        title={pageTitle}
        description={pageDesc}
        keywords="USFansSpreadsheet, przewodnik zakupowy, jak kupić, sklep, Kaspreadsheet, produkty, kategorie, Polska"
        canonicalPath={pagePath}
        ogType="article"
      />
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />

      <Navbar />

      <main>
        {/* Header */}
        <section className="pt-28 pb-8 md:pt-32 md:pb-12">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <nav className="flex items-center justify-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer">
                Strona główna
              </Link>
              <span className="text-foreground-400">/</span>
              <span className="text-accent-600 font-medium">Przewodnik zakupowy</span>
            </nav>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              Przewodnik zakupowy USFansSpreadsheet
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-4">
              Wszystko, co musisz wiedzieć przed zakupem produktów USFansSpreadsheet w oficjalnym sklepie Kaspreadsheet.
              Znajdź idealne produkty wśród naszych kategorii i ciesz się najwyższą jakością.
            </p>
            <a
              href={kaspreadsheetUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Przejdź do sklepu
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-cart-line text-xs"></i>
              </div>
            </a>
          </div>
        </section>

        {/* How to buy */}
        <section className="pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              <div className="bg-accent-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-200 mb-4">
                  <i className="ri-search-line text-accent-700 text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">1. Wybierz kategorię</h3>
                <p className="text-foreground-600 text-sm">Przeglądaj nasze kategorie produktów i znajdź to, czego potrzebujesz.</p>
              </div>
              <div className="bg-secondary-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-200 mb-4">
                  <i className="ri-shopping-cart-2-line text-secondary-700 text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">2. Dodaj do koszyka</h3>
                <p className="text-foreground-600 text-sm">Dodaj wybrane produkty do koszyka i przejdź do finalizacji zamówienia.</p>
              </div>
              <div className="bg-accent-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-200 mb-4">
                  <i className="ri-truck-line text-accent-700 text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">3. Odbierz przesyłkę</h3>
                <p className="text-foreground-600 text-sm">Twoje zamówienie zostanie wysłane i dostarczone pod wskazany adres.</p>
              </div>
            </div>

            {/* All categories */}
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              Wszystkie kategorie produktów
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 p-4 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className={`${cat.icon} text-accent-600 text-base`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">{t(cat.nameKey)}</span>
                  <div className="w-4 h-4 flex items-center justify-center ml-auto">
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                  </div>
                </a>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-12 bg-accent-100 rounded-lg p-8 text-center">
              <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                Gotowy na zakupy?
              </h3>
              <p className="text-foreground-600 text-sm mb-5 max-w-md mx-auto">
                Odwiedź oficjalny sklep Kaspreadsheet i odkryj pełną kolekcję produktów USFansSpreadsheet.
              </p>
              <a
                href={kaspreadsheetUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                buy it now
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </div>
              </a>
            </div>

            {/* Back to home */}
            <div className="mt-8 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-accent-600 hover:text-accent-700 text-sm font-medium transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
                Powrót do strony głównej
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}